import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import { RefreshCw, ExternalLink, Globe, AlertTriangle, CheckCircle2, Clock, Loader2, Trash2 } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("it-IT"); } catch { return "—"; }
};

const fmtDuration = (start, end) => {
  if (!start || !end) return "—";
  const s = new Date(start).getTime();
  const e = new Date(end).getTime();
  if (!Number.isFinite(s) || !Number.isFinite(e) || s <= 0 || e < s) return "—";
  const ms = e - s;
  if (ms < 1000) return "< 1s";
  if (ms < 60000) return `${Math.round(ms / 1000)}s`;
  const m = Math.floor(ms / 60000);
  const sec = Math.round((ms % 60000) / 1000);
  return `${m}m ${sec}s`;
};

const StatusBadge = ({ status }) => {
  if (status === "running") return <span className="badge badge-orange"><Loader2 size={11} className="animate-spin"/> in corso</span>;
  if (status === "queued") return <span className="badge badge-gray"><Clock size={11}/> in coda</span>;
  if (status === "completed") return <span className="badge badge-green"><CheckCircle2 size={11}/> completata</span>;
  if (status === "failed") return <span className="badge badge-red"><AlertTriangle size={11}/> fallita</span>;
  return <span className="badge badge-gray">{status}</span>;
};

const SemaforoStrip = ({ summary }) => {
  if (!summary?.semafori) return null;
  const map = [
    ["traffico", "Traf."],
    ["authority", "ZA"],
    ["pagespeed_mobile", "PS-M"],
    ["core_web_vitals", "CWV"],
    ["backlinks", "Link"],
    ["ai_readiness", "AI"],
  ];
  const color = (v) => v === "green" ? "var(--mv-success)" : v === "orange" ? "var(--mv-warning)" : v === "red" ? "var(--mv-danger)" : "#c5beb8";
  return (
    <div className="flex gap-2" style={{flexWrap:"wrap"}}>
      {map.map(([k, label]) => (
        <span key={k} className="text-small" style={{display:"inline-flex",alignItems:"center",gap:4,padding:"2px 6px",borderRadius:4,background:"var(--mv-bg)"}}>
          <span style={{width:8,height:8,borderRadius:"50%",background:color(summary.semafori[k])}}></span>
          {label}
        </span>
      ))}
    </div>
  );
};

export default function Coda() {
  const [active, setActive] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cleaning, setCleaning] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/queue");
      setActive(data.active || []);
      setCompleted(data.completed || []);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    reload();
    const hasRunning = active.some(a => ["running", "queued"].includes(a.status));
    const interval = hasRunning ? 3000 : 10000;
    const t = setInterval(reload, interval);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cleanupOrphans = async () => {
    if (!window.confirm("Eliminare analisi orfane e aziende residue di ricerche non aggiunte a liste?")) return;
    setCleaning(true);
    try {
      const { data } = await api.delete("/queue/orphans");
      await reload();
      alert(`Pulizia completata: ${data.removed_analyses} analisi orfane, ${data.removed_companies} aziende residue eliminate.`);
    } finally { setCleaning(false); }
  };

  const Row = ({ a, showSummary = false }) => {
    const c = a.company || {};
    const lst = a.list || {};
    const domain = a.analyzedDomain || c.website;
    return (
      <tr data-testid={`queue-row-${a.id}`}>
        <td style={{minWidth:220}}>
          <div style={{fontWeight:600,fontSize:14}}>{c.companyName || "Azienda rimossa"}</div>
          <div className="text-small text-muted" style={{marginTop:2}}>
            {c.city ? <>{c.city} ({c.province}) · </> : null}
            {lst.name ? (
              <Link to={`/liste/${lst.id}`} style={{color:"var(--mv-primary)"}}>{lst.name}</Link>
            ) : "—"}
          </div>
        </td>
        <td>
          {domain ? (
            <a href={`https://${domain}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-small">
              <Globe size={12}/> {domain}
            </a>
          ) : <span className="text-muted text-small">— no website —</span>}
        </td>
        <td><StatusBadge status={a.status} /></td>
        {showSummary && (
          <td>
            {a.summary ? (
              <div className="flex items-center gap-3">
                <div style={{fontWeight:700,fontSize:16,color: a.summary.score_globale >= 75 ? "var(--mv-success)" : a.summary.score_globale >= 50 ? "var(--mv-warning)" : "var(--mv-danger)"}}>
                  {a.summary.score_globale}
                </div>
                <SemaforoStrip summary={a.summary}/>
              </div>
            ) : "—"}
          </td>
        )}
        <td className="text-small text-muted">{fmtDate(a.startedAt)}</td>
        <td className="text-small">{fmtDuration(a.startedAt, a.completedAt)}</td>
        <td>
          {a.status === "failed" && a.errorMessage && (
            <span className="text-small" style={{color:"var(--mv-danger)"}} title={a.errorMessage}>
              {a.errorMessage.slice(0,40)}{a.errorMessage.length > 40 ? "…" : ""}
            </span>
          )}
          {a.status === "completed" && a.publicToken && (
            <a href={`/analisi/${a.publicToken}`} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" title="Apri report pubblico"><ExternalLink size={14}/></a>
          )}
        </td>
      </tr>
    );
  };

  return (
    <div data-testid="coda-page">
      <div className="flex justify-between items-center mb-6" style={{flexWrap:"wrap",gap:12}}>
        <div>
          <div className="h-display">Coda analisi</div>
          <div className="text-muted mt-2">Vista realtime — refresh ogni 3s mentre c'è almeno un'analisi attiva</div>
        </div>
        <div className="flex gap-2">
          <button onClick={cleanupOrphans} disabled={cleaning} className="btn btn-secondary btn-sm" data-testid="cleanup-orphans-btn" title="Elimina analisi orfane e aziende residue di ricerche">
            {cleaning ? <span className="spinner"/> : <><Trash2 size={14}/> Pulisci residui</>}
          </button>
          <button onClick={reload} disabled={loading} className="btn btn-secondary btn-sm">
            {loading ? <span className="spinner"/> : <><RefreshCw size={14}/> Aggiorna</>}
          </button>
        </div>
      </div>

      <div className="h-section mb-4">In coda / in corso ({active.length})</div>
      {active.length === 0 ? (
        <div className="card text-center text-muted" style={{padding:30}}>Nessuna analisi attiva.</div>
      ) : (
        <div className="card mb-8" style={{padding:0,overflow:"auto"}}>
          <table className="tbl">
            <thead>
              <tr>
                <th>Azienda / Lista</th>
                <th>Sito analizzato</th>
                <th>Stato</th>
                <th>Avvio</th>
                <th>Durata</th>
                <th>Dettagli</th>
              </tr>
            </thead>
            <tbody>{active.map(a => <Row key={a.id} a={a} />)}</tbody>
          </table>
        </div>
      )}

      <div className="h-section mb-4 mt-6">Completate di recente ({completed.length})</div>
      {completed.length === 0 ? (
        <div className="card text-center text-muted" style={{padding:30}}>Nessuna analisi completata ancora.</div>
      ) : (
        <div className="card" style={{padding:0,overflow:"auto"}}>
          <table className="tbl">
            <thead>
              <tr>
                <th>Azienda / Lista</th>
                <th>Sito analizzato</th>
                <th>Stato</th>
                <th>Risultato</th>
                <th>Avvio</th>
                <th>Durata</th>
                <th>Report</th>
              </tr>
            </thead>
            <tbody>{completed.map(a => <Row key={a.id} a={a} showSummary />)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
